/**
 * @file Parses the Analytics page URL's `tab`, `scope`, `period`, `status`
 * and `pastPromised` into an `AnalyticsView`. Consumer: the page's view hook,
 * at read time. `tab`, `scope` and `period` are matched by `===` against their
 * closed lists, and one that is missing or unknown falls back on its own to
 * `overview`, `ytd`, and `org` for a caller who can read Org or `self` for
 * anyone else. `status` and `pastPromised` are the Orders
 * filter and apply only on `orders`: `status` is one of
 * `ANALYTICS_ORDER_LIFECYCLES`, and `pastPromised` is true only for the
 * literal `true` and only without a status, so the two never combine; on any
 * other tab or value the canonical redirect drops the key. `project` is the
 * project detail's record id, read only on `projects`, an empty one as null.
 */
import { ANALYTICS_SCOPE_LEVELS } from '#types';

import { ANALYTICS_ORDER_LIFECYCLES } from './ANALYTICS_ORDER_LIFECYCLES';
import { ANALYTICS_PAGE_PERIODS } from './ANALYTICS_PAGE_PERIODS';
import { ANALYTICS_TABS } from './ANALYTICS_TABS';
import type { AnalyticsRights, AnalyticsView } from './index.types';

export const analyticsView = (
  searchParams: URLSearchParams,
  rights: Pick<AnalyticsRights, 'canReadOrg'>,
): AnalyticsView => {
  const tab =
    ANALYTICS_TABS.find((t) => t === searchParams.get('tab')) ?? 'overview';
  const isOrders = tab === 'orders';
  const status = isOrders
    ? (ANALYTICS_ORDER_LIFECYCLES.find(
        (lifecycle) => lifecycle === searchParams.get('status'),
      ) ?? null)
    : null;
  return {
    tab,
    scope:
      ANALYTICS_SCOPE_LEVELS.find(
        (scope) => scope === searchParams.get('scope'),
      ) ?? (rights.canReadOrg ? 'org' : 'self'),
    period:
      ANALYTICS_PAGE_PERIODS.find(
        (period) => period === searchParams.get('period'),
      ) ?? 'ytd',
    status,
    pastPromised:
      isOrders &&
      status === null &&
      searchParams.get('pastPromised') === 'true',
    project: (tab === 'projects' && searchParams.get('project')) || null,
  };
};
